import { obHoursApi } from '../services/obHoursApi.js'

// Round to quarter hours helper
const roundToQuarter = (num) => Math.round(num * 4) / 4;

// Calculate all hours with proper break deduction logic according to Swedish collective agreement
export function calculateAllHours(dateStr, startTime, endTime, breakDuration = 0) {
  if (!startTime || !endTime) {
    return { regular: 0, ob1: 0, ob2: 0, ob3: 0 };
  }

  // Convert break duration from minutes to hours
  const breakHours = parseFloat(breakDuration) / 60 || 0;
  
  // Calculate shift duration
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  let start = startH * 60 + startM;
  let end = endH * 60 + endM;
  if (end <= start) end += 24 * 60;
  const totalMinutes = end - start;
  const totalHours = totalMinutes / 60;

  // According to Swedish collective agreement:
  // - Workers get break only after 5+ hours
  // - Breaks are unpaid (not working time)
  // - If break falls during OB periods, OB time is reduced by break time
  
  let actualBreakHours = 0;
  if (totalHours >= 5 && breakHours > 0) {
    // Only apply break deduction if shift is 5+ hours AND break is specified
    actualBreakHours = breakHours;
  }

  // Use obHoursApi to calculate OB hours with proper holiday detection
  const workHours = totalHours - actualBreakHours;
  const obCalculation = obHoursApi.calculateOBHours(startTime, endTime, dateStr, workHours, breakDuration);
  
  // If it's a holiday, all working hours are OB3
  if (obCalculation.is_holiday) {
    return {
      regular: 0,
      ob1: 0,
      ob2: 0,
      ob3: roundToQuarter(workHours)
    };
  }

  // Calculate raw OB hours from the API (already includes holiday logic)
  const rawOB1 = obCalculation.ob1_hours;
  const rawOB2 = obCalculation.ob2_hours;
  const rawOB3 = obCalculation.ob3_hours;
  const totalRawOB = rawOB1 + rawOB2 + rawOB3;

  // Calculate regular hours (working hours - OB hours)
  let regularHours = workHours - totalRawOB;

  // Ensure regular hours are not negative
  regularHours = Math.max(0, regularHours);

  const result = {
    regular: roundToQuarter(regularHours),
    ob1: roundToQuarter(rawOB1),
    ob2: roundToQuarter(rawOB2),
    ob3: roundToQuarter(rawOB3)
  };

  return result;
}

// Helper function to calculate break overlap with specific OB period
function calculateBreakOverlapWithOBPeriod(dateStr, breakStart, breakEnd, obType) {
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay();
  
  const [breakStartH, breakStartM] = breakStart.split(':').map(Number);
  const [breakEndH, breakEndM] = breakEnd.split(':').map(Number);
  let breakStartMinutes = breakStartH * 60 + breakStartM;
  let breakEndMinutes = breakEndH * 60 + breakEndM;
  
  // Handle overnight breaks
  if (breakEndMinutes <= breakStartMinutes) {
    breakEndMinutes += 24 * 60;
  }
  
  let obStartMinutes, obEndMinutes;
  
  if (obType === 'OB1') {
    // OB1: Weekdays 18:15-20:00
    if (dayOfWeek === 0 || dayOfWeek === 6) return 0; // No OB1 on weekends
    obStartMinutes = 18 * 60 + 15;
    obEndMinutes = 20 * 60;
  } else if (obType === 'OB2') {
    // OB2: Weekdays 20:00-05:00 next day
    if (dayOfWeek < 1 || dayOfWeek > 5) return 0; // Only Mon-Fri
    obStartMinutes = 20 * 60;
    obEndMinutes = 29 * 60; // 05:00 next day
  } else if (obType === 'OB3') {
    // OB3: Saturday 12:00 to Monday 05:00
    if (dayOfWeek === 6) { // Saturday
      obStartMinutes = 12 * 60;
      obEndMinutes = 48 * 60 + 5 * 60; // Monday 05:00
    } else if (dayOfWeek === 0) { // Sunday
      obStartMinutes = 0;
      obEndMinutes = 29 * 60; // Monday 05:00
    } else if (dayOfWeek === 1) { // Monday
      obStartMinutes = 0;
      obEndMinutes = 5 * 60;
    } else {
      return 0;
    }
  }
  
  // Calculate overlap
  const overlapStart = Math.max(breakStartMinutes, obStartMinutes);
  const overlapEnd = Math.min(breakEndMinutes, obEndMinutes);
  
  if (overlapStart < overlapEnd) {
    return (overlapEnd - overlapStart) / 60; // Convert to hours
  }
  
  return 0;
}

// Break logic according to Swedish collective agreement (Handelsavtalet)
export function requiresLegalBreak(startTime, endTime) {
  if (!startTime || !endTime) return false
  const start = new Date(`2000-01-01 ${startTime}`)
  const end = new Date(`2000-01-01 ${endTime}`)
  if (end <= start) end.setDate(end.getDate() + 1)
  const diffMs = end - start
  const hours = diffMs / (1000 * 60 * 60)
  // Swedish law: Workers have right to break after 5+ hours of work
  return hours >= 5
}

// Calculate required break duration based on company policy (Handels kollektivavtal)
// - 5+ hours → 30 minutes
// - 7+ hours → 60 minutes
// - Over 8 hours → 75 minutes
export function calculateRequiredBreak(startTime, endTime) {
  if (!startTime || !endTime) return 0
  
  const start = new Date(`2000-01-01 ${startTime}`)
  const end = new Date(`2000-01-01 ${endTime}`)
  if (end <= start) end.setDate(end.getDate() + 1)
  const diffMs = end - start
  const hours = diffMs / (1000 * 60 * 60)
  
  if (hours > 8) {
    return 75 // 75 minutes for shifts over 8 hours
  } else if (hours >= 7) {
    return 60 // 60 minutes for 7-8 hour shifts
  } else if (hours >= 5) {
    return 30 // 30 minutes for 5+ hour shifts
  }
  return 0 // No break for shorter shifts
}

// Calculate when break typically occurs (after initial work period)
export function calculateBreakTime(startTime, endTime, breakDuration = null) {
  // Use required break duration if not provided
  const duration = breakDuration !== null ? breakDuration : calculateRequiredBreak(startTime, endTime)
  
  if (duration === 0) {
    return null // No break required
  }
  
  const [startH, startM] = startTime.split(':').map(Number)
  const startMinutes = startH * 60 + startM
  
  // Break typically starts after initial work period (varies by shift length)
  // For longer shifts, break comes later
  const [endH, endM] = endTime.split(':').map(Number)
  let endMinutes = endH * 60 + endM
  if (endMinutes <= startMinutes) endMinutes += 24 * 60
  const shiftMinutes = endMinutes - startMinutes
  
  // Break typically occurs around the middle of the shift
  const breakStartMinutes = startMinutes + Math.floor(shiftMinutes / 2) - Math.floor(duration / 2)
  const breakEndMinutes = breakStartMinutes + duration
  
  const breakStartH = Math.floor(breakStartMinutes / 60) % 24
  const breakStartM = breakStartMinutes % 60
  const breakEndH = Math.floor(breakEndMinutes / 60) % 24
  const breakEndM = breakEndMinutes % 60
  
  return {
    start: `${String(breakStartH).padStart(2, '0')}:${String(breakStartM).padStart(2, '0')}`,
    end: `${String(breakEndH).padStart(2, '0')}:${String(breakEndM).padStart(2, '0')}`,
    duration: duration
  }
}

// Working days count
export function workingDaysCount(timeEntries, currentDate) {
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  return Object.keys(timeEntries)
    .filter(date => {
      const entryDate = new Date(date)
      return entryDate.getFullYear() === year && entryDate.getMonth() === month && 
             timeEntries[date].length > 0
    })
    .length
}

// Average hours per day
export function averageHoursPerDay(monthlyHours, workingDaysCount) {
  if (workingDaysCount === 0) return '0.00'
  const average = parseFloat(monthlyHours) / workingDaysCount
  return average.toFixed(2)
}

// Universal monthly hours calculator - works for both assignedShifts and timeEntries
export function monthlyHours(data, currentDate = null) {
  // If currentDate is provided, filter by year/month to ensure accuracy
  const filterByMonth = currentDate !== null;
  const targetYear = filterByMonth ? currentDate.getFullYear() : null;
  const targetMonth = filterByMonth ? currentDate.getMonth() : null;
  
  const result = Object.entries(data).reduce((total, [dateKey, entries]) => {
    if (!Array.isArray(entries)) return total;
    
    // If filtering by month, check if this date belongs to the target month
    if (filterByMonth) {
      const entryDate = new Date(dateKey);
      const localYear = entryDate.getFullYear();
      const localMonth = entryDate.getMonth();
      const matches = localYear === targetYear && localMonth === targetMonth;
      
      if (!matches) {
        return total; // Skip this date, it's not in the target month
      }
    }
    
    const dayTotal = entries.reduce((dayTotal, entry) => {
      let hours = 0;
      
      // Priority 1: If entry has totalHours (calculated time entries), use it directly
      if (entry.totalHours !== undefined) {
        hours = parseFloat(entry.totalHours) || 0;
      }
      // Priority 2: If it's a shift with start_time/end_time, calculate hours using Swedish collective agreement
      else if (entry.start_time && entry.end_time) {
        // Handle both full datetime strings and HH:MM format
        let startTime = entry.start_time;
        let endTime = entry.end_time;
        
        // Extract time portion if it's a full datetime string
        if (startTime.includes('T')) {
          startTime = startTime.split('T')[1].substring(0, 5);
        }
        if (endTime.includes('T')) {
          endTime = endTime.split('T')[1].substring(0, 5);
        }
        
        // Use calculateAllHours for proper Swedish collective agreement break deduction
        const breakDuration = entry.break_duration || 0;
        const allHours = calculateAllHours(dateKey, startTime, endTime, breakDuration);
        
        // Sum all hour types for total hours (with proper break deduction already applied)
        hours = allHours.regular + allHours.ob1 + allHours.ob2 + allHours.ob3;
      }
      
      return dayTotal + (hours > 0 ? hours : 0);
    }, 0);
    
    return total + dayTotal;
  }, 0);
  
  return result;
}

// Day total hours
export function getDayTotalHours(entries) {
  if (!entries || entries.length === 0) return '0.00'
  // Use the composable's formatQuarterHours
  const { formatQuarterHours } = useTimeCalculations();
  const total = entries.reduce((sum, entry) => sum + parseFloat(entry.totalHours), 0)
  return formatQuarterHours(total)
}

// Day status
export function getDayStatus(entries) {
  if (!entries || entries.length === 0) return 'draft'
  if (entries.some(e => e.status === 'rejected')) return 'rejected'
  if (entries.some(e => e.status === 'approved')) return 'approved'
  if (entries.some(e => e.status === 'submitted')) return 'submitted'
  return 'draft'
}
// OB1 and OB2 calculation helpers
function calculateOB1Hours(dateStr, startTime, endTime, breakDuration = 0) {
  if (!startTime || !endTime) return '0.00';
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) return '0.00'; // Weekend, no OB1
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  let start = startH * 60 + startM;
  let end = endH * 60 + endM;
  if (end <= start) end += 24 * 60;
  const ob1Start = 18 * 60 + 15;
  const ob1End = 20 * 60;
  let overlapStart = Math.max(start, ob1Start);
  let overlapEnd = Math.min(end, ob1End);
  let ob1Minutes = 0;
  if (overlapStart < overlapEnd) ob1Minutes = overlapEnd - overlapStart;
  
  // Break deduction is handled in the parent calculation, not here
  const hours = ob1Minutes / 60;
  // Round to nearest 0.25 (quarter hour)
  return (Math.round(hours * 4) / 4).toFixed(2);
}

function calculateOB2Hours(dateStr, startTime, endTime, breakDuration = 0) {
  if (!startTime || !endTime) return '0.00';
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay();
  // Only apply OB2 for Mon-Fri (1-5)
  if (dayOfWeek < 1 || dayOfWeek > 5) return '0.00';
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  let start = startH * 60 + startM;
  let end = endH * 60 + endM;
  let ob2Minutes = 0;

  // If overnight shift (end <= start), extend end to next day
  if (end <= start) end += 24 * 60;

  // OB2 window: 20:00 (1200) to 05:00 next day (1800 + 300 = 1500)
  // Check overlap with 20:00–24:00 on current day
  const ob2StartToday = 20 * 60; // 1200
  const ob2EndToday = 24 * 60;   // 1440
  let overlapStartToday = Math.max(start, ob2StartToday);
  let overlapEndToday = Math.min(end, ob2EndToday);
  if (overlapStartToday < overlapEndToday) ob2Minutes += overlapEndToday - overlapStartToday;

  // Check overlap with 00:00–05:00 on next day
  const ob2StartNextDay = 24 * 60; // 1440
  const ob2EndNextDay = 29 * 60;   // 1740
  let overlapStartNextDay = Math.max(start, ob2StartNextDay);
  let overlapEndNextDay = Math.min(end, ob2EndNextDay);
  if (overlapStartNextDay < overlapEndNextDay) ob2Minutes += overlapEndNextDay - overlapStartNextDay;

  // Break deduction is handled in the parent calculation, not here
  const hours = ob2Minutes / 60;
  // Round to nearest 0.25 (quarter hour)
  return (Math.round(hours * 4) / 4).toFixed(2);
}
// OB3 calculation
export function calculateOB3Hours(dateStr, startTime, endTime, breakDuration = 0) {
  if (!startTime || !endTime) return '0.00';
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay();
  let ob3Start, ob3End;
  if (dayOfWeek === 6) { // Saturday
    ob3Start = 12 * 60; // 12:00 Saturday
    ob3End = 48 * 60 + 5 * 60; // 05:00 Monday (12:00 Sat + 24h Sun + 5h Mon = 53h)
  } else if (dayOfWeek === 0) { // Sunday
    ob3Start = 0; // 00:00 Sunday
    ob3End = 29 * 60; // 05:00 Monday (24h Sun + 5h Mon)
  } else if (dayOfWeek === 1) { // Monday
    ob3Start = 0; // 00:00 Monday
    ob3End = 5 * 60; // 05:00 Monday
  } else {
    return '0.00';
  }
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  let start = startH * 60 + startM;
  let end = endH * 60 + endM;
  if (end <= start) end += 24 * 60;
  let overlapStart = Math.max(start, ob3Start);
  let overlapEnd = Math.min(end, ob3End);
  let ob3Minutes = 0;
  if (overlapStart < overlapEnd) ob3Minutes = overlapEnd - overlapStart;
  
  // Break deduction is handled in the parent calculation, not here
  const hours = ob3Minutes / 60;
  // Round to nearest 0.25 (quarter hour)
  return (Math.round(hours * 4) / 4).toFixed(2);
}
// Composable for time and OB calculations used in TimeReporting.vue
import { ref } from 'vue'

export function useTimeCalculations() {
  function parseTimeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number)
    return hours * 60 + minutes
  }

  function roundToQuarterHour(hours) {
    return Math.round(hours * 4) / 4
  }

  function formatQuarterHours(hours) {
    return roundToQuarterHour(hours).toFixed(2)
  }

  function calculateTotalHours(startTime, endTime, breakDuration = 0) {
    if (!startTime || !endTime) return '0.00'
    // Parse times as minutes since midnight
    const [startH, startM] = startTime.split(':').map(Number)
    const [endH, endM] = endTime.split(':').map(Number)
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    let durationMinutes = endMinutes - startMinutes;
    // If end is before start, assume overnight shift
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
    }
    durationMinutes -= parseInt(breakDuration) || 0;
    const hours = durationMinutes / 60;
    return formatQuarterHours(Math.max(0, hours));
  }

  function isOB3Period(selectedDate, startTime, endTime) {
    if (!selectedDate) return { isOB3: false, ob3StartTime: 0, ob3EndTime: 0 }
    const workDate = new Date(selectedDate)
    const dayOfWeek = workDate.getDay()
    let isOB3 = false
    let ob3StartTime = 0
    let ob3EndTime = 24 * 60 - 1
    if (dayOfWeek === 6) {
      isOB3 = true
      ob3StartTime = 12 * 60
    } else if (dayOfWeek === 0) {
      isOB3 = true
      ob3StartTime = 0
    }
    return { isOB3, ob3StartTime, ob3EndTime }
  }

  // Format datetime string to HH:MM display format
  function formatTime(timeString) {
    const date = new Date(timeString)
    return date.toLocaleTimeString('sv-SE', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    })
  }

  // Calculate shift duration in human readable format (e.g., "7h" for 8h shift with 1h break)
  // This shows NET working hours (total time minus break)
  function calculateShiftDuration(startTime, endTime) {
    const start = new Date(startTime)
    const end = new Date(endTime)
    const durationMs = end - start
    const totalHours = durationMs / (1000 * 60 * 60)
    
    // Extract just the time portion for break calculation
    let startTimeStr = startTime
    let endTimeStr = endTime
    if (startTime.includes('T')) {
      startTimeStr = startTime.split('T')[1].substring(0, 5)
    }
    if (endTime.includes('T')) {
      endTimeStr = endTime.split('T')[1].substring(0, 5)
    }
    
    // Calculate required break in minutes
    const breakMinutes = calculateRequiredBreak(startTimeStr, endTimeStr)
    
    // Subtract break time from total duration
    const netHours = totalHours - (breakMinutes / 60)
    const hours = Math.floor(netHours)
    const minutes = Math.round((netHours - hours) * 60)
    
    if (minutes === 0) {
      return `${hours}h`
    } else {
      return `${hours}h ${minutes}m`
    }
  }

  return {
    parseTimeToMinutes,
    roundToQuarterHour,
    formatQuarterHours,
    calculateTotalHours,
    isOB3Period,
    calculateOB1Hours,
    calculateOB2Hours,
    calculateOB3Hours,
    calculateAllHours,
    formatTime,
    calculateShiftDuration,
    calculateBreakTime,
    calculateRequiredBreak
  }
}
